import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { dashboardAPI, devisAPI, portalRequestsAPI, usersAPI, dashboardStatsAPI, statsAPI } from '@/lib/api';
import { formatPrice, formatDateTime, getStatusColor, getStatusLabel, getActivityIcon } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  FileText,
  Users,
  Euro,
  TrendingUp,
  Plus,
  Phone,
  MessageSquare,
  Mail,
  StickyNote,
  Clock,
  ArrowRight,
  Send,
  CheckCircle,
  XCircle,
  FileEdit,
  RefreshCw,
  AlertCircle,
  Globe,
  Bell,
  UserPlus,
  Loader2,
} from 'lucide-react';
import AIAgentChat from '@/components/AIAgentChat';

const DashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentDevis, setRecentDevis] = useState([]);
  const [activities, setActivities] = useState([]);
  const [portalRequests, setPortalRequests] = useState([]);
  const [users, setUsers] = useState([]);
  const [statsByVendeur, setStatsByVendeur] = useState([]);
  const [caMonthly, setCAMonthly] = useState({ months: [], data: [] });
  const [loading, setLoading] = useState(true);
  const [assigningRequest, setAssigningRequest] = useState(null);
  const [creatingDevis, setCreatingDevis] = useState(null);
  const [devisToAssign, setDevisToAssign] = useState(0);

  const isAdmin = () => user?.role === 'admin';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const promises = [
        dashboardAPI.getStats(),
        devisAPI.getAll(),
        dashboardAPI.getRecentActivities(),
        portalRequestsAPI.getAll({ status: 'nouveau', limit: 20 }),
      ];
      
      // Admin gets user list and stats by vendeur too
      if (isAdmin()) {
        promises.push(usersAPI.getAll());
        promises.push(dashboardStatsAPI.getStatsByVendeur());
        promises.push(dashboardStatsAPI.getCAMonthly());
        promises.push(statsAPI.getDevisToAssignCount());
      }

      const results = await Promise.all(promises);
      
      setStats(results[0].data);
      // Filter devis by user if not admin
      const allDevis = results[1].data;
      setRecentDevis(isAdmin() ? allDevis.slice(0, 5) : allDevis.filter(d => d.assigned_to === user.id).slice(0, 5));
      setActivities(results[2].data);
      setPortalRequests(results[3].data || []);
      if (results[4]) {
        setUsers(results[4].data || []);
      }
      if (results[5]) {
        setStatsByVendeur(results[5].data || []);
      }
      if (results[6]) {
        setCAMonthly(results[6].data || { months: [], data: [] });
      }
      if (results[7]) {
        setDevisToAssign(results[7].data?.count || 0);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsProcessed = async (requestId) => {
    try {
      await portalRequestsAPI.updateStatus(requestId, { status: 'traite' });
      setPortalRequests(prev => prev.filter(r => r.id !== requestId));
      // Update stats
      setStats(prev => ({
        ...prev,
        nouvelles_demandes_portail: Math.max(0, (prev?.nouvelles_demandes_portail || 1) - 1)
      }));
    } catch (error) {
      console.error('Error updating request:', error);
    }
  };

  const handleAssignVendeur = async (requestId, vendeurId) => {
    setAssigningRequest(requestId);
    try {
      await portalRequestsAPI.assign(requestId, vendeurId);
      const vendeur = users.find(u => u.id === vendeurId);
      toast.success(`Demande assignée à ${vendeur?.name || 'vendeur'}`);
      // Update local state
      setPortalRequests(prev => prev.map(r => 
        r.id === requestId ? { ...r, assigned_to: vendeurId, assigned_to_name: vendeur?.name } : r
      ));
    } catch (error) {
      console.error('Error assigning:', error);
      toast.error("Erreur lors de l'assignation");
    } finally {
      setAssigningRequest(null);
    }
  };

  const handleCreateDevisFromRequest = async (request) => {
    setCreatingDevis(request.id);
    try {
      const result = await portalRequestsAPI.createDevis(request.id);
      toast.success('Devis créé avec succès');
      // Navigate to the new devis
      navigate(`/admin/devis/${result.data.devis_id}/modifier`);
    } catch (error) {
      console.error('Error creating devis:', error);
      toast.error("Erreur lors de la création du devis");
    } finally {
      setCreatingDevis(null);
    }
  };

  const getActivityIconComponent = (type) => {
    const icons = {
      appel: Phone,
      sms: MessageSquare,
      mail: Mail,
      note: StickyNote,
    };
    return icons[type] || Clock;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-800"></div>
      </div>
    );
  }

  // Calculate stats by status
  const statusStats = [
    { 
      key: 'brouillon', 
      label: 'Brouillons', 
      count: stats?.brouillon || 0, 
      ca: stats?.ca_brouillon || 0,
      icon: FileEdit,
      bgColor: 'bg-slate-50',
      textColor: 'text-slate-600',
      iconBg: 'bg-slate-100'
    },
    { 
      key: 'en_cours', 
      label: 'En cours', 
      count: (stats?.envoye || 0) + (stats?.relance_1 || 0) + (stats?.relance_2 || 0) + (stats?.relance_3 || 0), 
      ca: stats?.ca_en_cours || 0,
      icon: Send,
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-600',
      iconBg: 'bg-blue-100'
    },
    { 
      key: 'accepte', 
      label: 'Acceptés', 
      count: stats?.accepte || 0, 
      ca: stats?.ca_total || 0,
      icon: CheckCircle,
      bgColor: 'bg-green-50',
      textColor: 'text-green-600',
      iconBg: 'bg-green-100'
    },
    { 
      key: 'refuse', 
      label: 'Refusés / Sans suite', 
      count: (stats?.refuse || 0) + (stats?.sans_suite || 0), 
      ca: stats?.ca_refuse || 0,
      icon: XCircle,
      bgColor: 'bg-red-50',
      textColor: 'text-red-600',
      iconBg: 'bg-red-100'
    },
  ];

  // Devis à relancer (envoye, relance_1, relance_2, relance_3)
  const devisARelancer = recentDevis.filter(d => 
    ['envoye', 'relance_1', 'relance_2', 'relance_3'].includes(d.status)
  );

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Barlow Condensed' }}>
            Bonjour, {user?.name}
          </h1>
          <p className="text-slate-500 mt-1">Voici un aperçu de votre activité</p>
        </div>
        <Link to="/admin/devis/nouveau">
          <Button className="bg-orange-500 hover:bg-orange-600" data-testid="new-devis-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nouveau devis
          </Button>
        </Link>
      </div>

      {/* Main Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-slate-200 bg-gradient-to-br from-slate-800 to-slate-900 text-white" data-testid="stat-total-devis">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-300 uppercase tracking-wide">Total Devis</p>
                <p className="text-4xl font-bold mt-1" style={{ fontFamily: 'Barlow Condensed' }}>
                  {stats?.total_devis || 0}
                </p>
                <p className="text-sm text-slate-400 mt-1">
                  CA potentiel: {formatPrice(stats?.ca_global || 0)}
                </p>
              </div>
              <div className="w-14 h-14 rounded-lg bg-white/10 flex items-center justify-center">
                <FileText className="w-7 h-7 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 bg-gradient-to-br from-green-500 to-green-600 text-white" data-testid="stat-acceptes">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-100 uppercase tracking-wide">Commandes</p>
                <p className="text-4xl font-bold mt-1" style={{ fontFamily: 'Barlow Condensed' }}>
                  {stats?.accepte || 0}
                </p>
                <p className="text-sm text-green-200 mt-1">
                  CA: {formatPrice(stats?.ca_total || 0)}
                </p>
              </div>
              <div className="w-14 h-14 rounded-lg bg-white/10 flex items-center justify-center">
                <CheckCircle className="w-7 h-7 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200" data-testid="stat-clients">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 uppercase tracking-wide">Contacts</p>
                <p className="text-4xl font-bold text-slate-800 mt-1" style={{ fontFamily: 'Barlow Condensed' }}>
                  {stats?.total_clients || 0}
                </p>
                <p className="text-sm text-slate-400 mt-1">
                  Base clients
                </p>
              </div>
              <div className="w-14 h-14 rounded-lg bg-blue-100 flex items-center justify-center">
                <Users className="w-7 h-7 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 border-orange-200 bg-orange-50" data-testid="stat-a-relancer">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-orange-600 uppercase tracking-wide">À relancer</p>
                <p className="text-4xl font-bold text-orange-600 mt-1" style={{ fontFamily: 'Barlow Condensed' }}>
                  {stats?.total_a_relancer || 0}
                </p>
                <p className="text-sm text-orange-500 mt-1">
                  CA: {formatPrice(stats?.ca_en_cours || 0)}
                </p>
              </div>
              <div className="w-14 h-14 rounded-lg bg-orange-100 flex items-center justify-center">
                <RefreshCw className="w-7 h-7 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Carte Devis à affecter - Visible uniquement pour les admins */}
        {isAdmin() && devisToAssign > 0 && (
          <Card 
            className="border-red-300 bg-gradient-to-br from-red-500 to-red-600 text-white cursor-pointer hover:from-red-600 hover:to-red-700 transition-all" 
            data-testid="stat-to-assign"
            onClick={() => navigate('/admin/devis?status=brouillon&unassigned=true')}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-red-100 uppercase tracking-wide flex items-center gap-2">
                    <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
                    À affecter
                  </p>
                  <p className="text-4xl font-bold mt-1" style={{ fontFamily: 'Barlow Condensed' }}>
                    {devisToAssign}
                  </p>
                  <p className="text-sm text-red-200 mt-1">
                    Leads en attente
                  </p>
                </div>
                <div className="w-14 h-14 rounded-lg bg-white/10 flex items-center justify-center">
                  <UserPlus className="w-7 h-7 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Alert for devis to follow up */}
      {(stats?.total_a_relancer || 0) > 0 && (
        <Card className="border-orange-300 bg-orange-50" data-testid="alert-relance">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-orange-600" />
              <p className="text-orange-800 flex-1">
                <strong>{stats?.total_a_relancer}</strong> devis en attente de relance • 
                Montant total: <strong>{formatPrice(stats?.ca_en_cours || 0)}</strong>
              </p>
              <Link to="/admin/devis?relance=true">
                <Button size="sm" className="bg-orange-500 hover:bg-orange-600 text-white">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Voir les devis
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      )}

      {/* AI Sales Agent Chat */}
      <AIAgentChat compact={true} />

      {/* New Portal Requests */}
      {portalRequests.length > 0 && (
        <Card className="border-yellow-300 bg-yellow-50" data-testid="portal-requests">
          <CardHeader className="border-b border-yellow-200 bg-yellow-100/50 py-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-yellow-800" style={{ fontFamily: 'Barlow Condensed' }}>
                <Bell className="w-5 h-5 animate-pulse" />
                Nouvelles demandes du portail ({portalRequests.length})
              </CardTitle>
              <Badge className="bg-yellow-500 text-white">
                <Globe className="w-3 h-3 mr-1" />
                Portail Web
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="p-4 space-y-3">
            {portalRequests.map((request) => (
              <div 
                key={request.id} 
                className="bg-white rounded-lg p-4 border border-yellow-200 shadow-sm"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-semibold text-slate-800">{request.client_name}</span>
                      <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded">
                        #{request.reference}
                      </span>
                      {request.wants_demo && (
                        <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
                          🎯 Démo
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-slate-600 space-y-1">
                      <p className="flex items-center gap-2">
                        <Mail className="w-3 h-3" />
                        <a href={`mailto:${request.client_email}`} className="hover:text-yellow-600">
                          {request.client_email}
                        </a>
                      </p>
                      <p className="flex items-center gap-2">
                        <Phone className="w-3 h-3" />
                        <a href={`tel:${request.client_phone}`} className="hover:text-yellow-600">
                          {request.client_phone}
                        </a>
                      </p>
                      {request.machine_name && (
                        <p className="text-xs text-slate-500">
                          Machine: <strong>{request.machine_name}</strong>
                        </p>
                      )}
                      {request.message && (
                        <p className="text-xs text-slate-500 italic line-clamp-2">
                          "{request.message}"
                        </p>
                      )}
                    </div>
                    <p className="text-xs text-slate-400 mt-2">
                      {formatDateTime(request.created_at)}
                    </p>
                  </div>
                  <div className="flex flex-col gap-2 min-w-[140px]">
                    {/* Assign vendeur (Admin only) */}
                    {isAdmin() && (
                      <Select
                        value={request.assigned_to || 'none'}
                        onValueChange={(value) => handleAssignVendeur(request.id, value === 'none' ? null : value)}
                        disabled={assigningRequest === request.id}
                      >
                        <SelectTrigger className="h-8 text-xs">
                          {assigningRequest === request.id ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <UserPlus className="w-3 h-3 mr-1" />
                          )}
                          <SelectValue placeholder="Assigner" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Non assigné</SelectItem>
                          {users.map(u => (
                            <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    {/* Show assigned name for non-admin */}
                    {!isAdmin() && request.assigned_to_name && (
                      <Badge variant="outline" className="text-xs justify-center">
                        {request.assigned_to_name}
                      </Badge>
                    )}
                    <Button 
                      size="sm" 
                      className="bg-yellow-500 hover:bg-yellow-600 text-white w-full"
                      onClick={() => handleCreateDevisFromRequest(request)}
                      disabled={creatingDevis === request.id}
                    >
                      {creatingDevis === request.id ? (
                        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                      ) : (
                        <FileText className="w-3 h-3 mr-1" />
                      )}
                      Créer devis
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => handleMarkAsProcessed(request.id)}
                      className="border-slate-300 text-slate-600 hover:bg-slate-100"
                    >
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Traité
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Status Breakdown with CA */}
      <Card className="border-slate-200">
        <CardHeader className="border-b border-slate-100 bg-slate-50/50">
          <CardTitle style={{ fontFamily: 'Barlow Condensed' }}>Répartition par statut</CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {statusStats.map((status) => (
              <div key={status.key} className={`${status.bgColor} rounded-lg p-4`}>
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-10 h-10 rounded-lg ${status.iconBg} flex items-center justify-center`}>
                    <status.icon className={`w-5 h-5 ${status.textColor}`} />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-600">{status.label}</p>
                    <p className={`text-2xl font-bold ${status.textColor}`} style={{ fontFamily: 'Barlow Condensed' }}>
                      {status.count}
                    </p>
                  </div>
                </div>
                <div className="border-t border-slate-200/50 pt-3">
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Montant</p>
                  <p className={`text-lg font-mono font-semibold ${status.textColor}`}>
                    {formatPrice(status.ca)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Stats by Vendeur - Admin only */}
      {isAdmin() && statsByVendeur.length > 0 && (
        <Card className="border-slate-200" data-testid="stats-by-vendeur">
          <CardHeader className="border-b border-slate-100 bg-slate-50/50">
            <CardTitle style={{ fontFamily: 'Barlow Condensed' }}>Performance par vendeur</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50/50">
                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wide p-4">Vendeur</th>
                    <th className="text-center text-xs font-semibold text-slate-500 uppercase tracking-wide p-4">Devis</th>
                    <th className="text-center text-xs font-semibold text-slate-500 uppercase tracking-wide p-4">Envoyés</th>
                    <th className="text-center text-xs font-semibold text-slate-500 uppercase tracking-wide p-4">Acceptés</th>
                    <th className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wide p-4">CA Accepté</th>
                    <th className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wide p-4 hidden md:table-cell">CA En cours</th>
                    <th className="text-center text-xs font-semibold text-slate-500 uppercase tracking-wide p-4 hidden lg:table-cell">Contacts</th>
                    <th className="text-center text-xs font-semibold text-slate-500 uppercase tracking-wide p-4 hidden lg:table-cell">RDV</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {statsByVendeur.map((vendeur) => (
                    <tr key={vendeur.user_id} className="hover:bg-slate-50">
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-slate-800 text-white flex items-center justify-center text-sm font-medium">
                            {vendeur.user_name?.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-medium text-slate-800">{vendeur.user_name}</p>
                            <p className="text-xs text-slate-500 capitalize">{vendeur.role}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-4 text-center">
                        <span className="font-mono text-slate-800">{vendeur.total_devis}</span>
                      </td>
                      <td className="p-4 text-center">
                        <span className="font-mono text-blue-600">{vendeur.devis_envoyes}</span>
                      </td>
                      <td className="p-4 text-center">
                        <span className="font-mono text-green-600 font-semibold">{vendeur.devis_acceptes}</span>
                      </td>
                      <td className="p-4 text-right">
                        <span className="font-mono text-green-600 font-semibold">{formatPrice(vendeur.ca_accepte)}</span>
                      </td>
                      <td className="p-4 text-right hidden md:table-cell">
                        <span className="font-mono text-orange-600">{formatPrice(vendeur.ca_en_cours)}</span>
                      </td>
                      <td className="p-4 text-center hidden lg:table-cell">
                        <span className="font-mono text-slate-600">{vendeur.clients_count}</span>
                      </td>
                      <td className="p-4 text-center hidden lg:table-cell">
                        <span className="font-mono text-slate-600">{vendeur.appointments_count}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* CA Mensuel sur 12 mois - Admin only */}
      {isAdmin() && caMonthly.data.length > 0 && (
        <Card className="border-slate-200" data-testid="ca-monthly-table">
          <CardHeader className="border-b border-slate-100 bg-slate-50/50">
            <CardTitle style={{ fontFamily: 'Barlow Condensed' }}>CA Accepté sur 12 mois (par vendeur)</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50/50">
                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wide p-3 sticky left-0 bg-slate-50/50">Vendeur</th>
                    {caMonthly.months.map(month => (
                      <th key={month} className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wide p-3 min-w-[80px]">
                        {month}
                      </th>
                    ))}
                    <th className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wide p-3 bg-slate-100">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {caMonthly.data.map((vendeur) => (
                    <tr key={vendeur.user_id} className="hover:bg-slate-50">
                      <td className="p-3 sticky left-0 bg-white font-medium text-slate-800">
                        {vendeur.user_name}
                      </td>
                      {caMonthly.months.map(month => (
                        <td key={month} className="p-3 text-right">
                          <span className={`font-mono ${vendeur.monthly_ca[month] > 0 ? 'text-green-600' : 'text-slate-300'}`}>
                            {vendeur.monthly_ca[month] > 0 ? formatPrice(vendeur.monthly_ca[month]) : '-'}
                          </span>
                        </td>
                      ))}
                      <td className="p-3 text-right bg-slate-50">
                        <span className="font-mono font-semibold text-green-700">
                          {formatPrice(vendeur.total_ca)}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {/* Total row */}
                  <tr className="bg-slate-100 font-semibold">
                    <td className="p-3 sticky left-0 bg-slate-100 text-slate-800">Total</td>
                    {caMonthly.months.map(month => {
                      const monthTotal = caMonthly.data.reduce((sum, v) => sum + (v.monthly_ca[month] || 0), 0);
                      return (
                        <td key={month} className="p-3 text-right">
                          <span className="font-mono text-green-700">
                            {monthTotal > 0 ? formatPrice(monthTotal) : '-'}
                          </span>
                        </td>
                      );
                    })}
                    <td className="p-3 text-right bg-slate-200">
                      <span className="font-mono font-bold text-green-800">
                        {formatPrice(caMonthly.data.reduce((sum, v) => sum + v.total_ca, 0))}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Devis */}
        <Card className="border-slate-200">
          <CardHeader className="border-b border-slate-100 bg-slate-50/50 flex flex-row items-center justify-between">
            <CardTitle style={{ fontFamily: 'Barlow Condensed' }}>Devis récents</CardTitle>
            <Link to="/admin/devis">
              <Button variant="ghost" size="sm" data-testid="view-all-devis-btn">
                Voir tout
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent className="p-0">
            {recentDevis.length === 0 ? (
              <div className="p-6 text-center text-slate-500">
                Aucun devis pour le moment
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {recentDevis.map((devis) => (
                  <Link
                    key={devis.id}
                    to={`/admin/devis/${devis.id}`}
                    className="flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
                    data-testid={`devis-item-${devis.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-800 font-mono text-sm">
                        {devis.reference}
                      </p>
                      <p className="text-sm text-slate-500 truncate">
                        {devis.client_name} - {devis.mini_pelle_name}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge className={getStatusColor(devis.status)}>
                        {getStatusLabel(devis.status)}
                      </Badge>
                      <span className="font-mono text-sm font-medium text-slate-800">
                        {formatPrice(devis.total)}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Activities */}
        <Card className="border-slate-200">
          <CardHeader className="border-b border-slate-100 bg-slate-50/50">
            <CardTitle style={{ fontFamily: 'Barlow Condensed' }}>Activités récentes</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {activities.length === 0 ? (
              <div className="p-6 text-center text-slate-500">
                Aucune activité récente
              </div>
            ) : (
              <div className="divide-y divide-slate-100 max-h-96 overflow-y-auto">
                {activities.slice(0, 10).map((activity, index) => {
                  const IconComponent = getActivityIconComponent(activity.type);
                  const iconStyle = getActivityIcon(activity.type);
                  
                  return (
                    <div key={index} className="flex gap-3 p-4">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${iconStyle.color}`}>
                        <IconComponent className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-slate-800">
                          <span className="font-medium">{activity.created_by_name}</span>
                          {' - '}
                          <span className="capitalize">{activity.type}</span>
                        </p>
                        <p className="text-sm text-slate-500 truncate">{activity.content}</p>
                        <p className="text-xs text-slate-400 mt-1">
                          {activity.devis_reference} • {formatDateTime(activity.created_at)}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DashboardPage;
